import * as React from 'react';
import moment from 'moment-timezone';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { SavingState } from 'api/savingState';
import { cloneDeep, isEqual } from 'lodash';
import { logDifference } from 'utils/helper';
import {
    addArtikelDetailLieferant,
    ArtikelDetailLieferant,
    defaultArtikelDetailLieferant,
    updateArtikelDetailLieferant,
    useArtikelDetailLieferant,
} from 'api/artikelDetailLieferant';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { formStyles } from 'styles/formStyles';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { Timestamps } from 'formComponents/Timestamps';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector } from 'store/lookups/selector';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { createKundeUrl } from 'utils/formatter';
import { useKunde } from 'api/kunde';

interface ArtikelDetailLieferantEditProps {
    artikelGruppeNr: number;
    artikelNr: string;
    lNr: number; // -1 = new
    onClose(refresh: boolean): void;
}

export const ArtikelDetailLieferantEdit: React.FC<ArtikelDetailLieferantEditProps> = ({
    artikelGruppeNr,
    artikelNr,
    lNr,
    onClose,
}) => {
    const artikelGruppeByNr = useSelector(artikelGruppenByNrSelector);
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);

    const [artikelDetailLieferant, setArtikelDetailLieferant] = React.useState<ArtikelDetailLieferant | null>(null);

    const dbArtikelDetailLieferant = useArtikelDetailLieferant(
        {
            artikelGruppeNr,
            artikelNr,
            lNr,
        },
        {
            enabled: lNr !== -1,
            onSuccess: (data) => {
                setSavingState(SavingState.notChanged);
                setArtikelDetailLieferant(cloneDeep(data));
            },
        },
    );

    React.useEffect(() => {
        if (lNr === -1) {
            const now = moment();

            setArtikelDetailLieferant({
                ...defaultArtikelDetailLieferant,
                artikelGruppeNr,
                artikelNr,
                ekGiltAb: now.format('YYYY-MM-DD') + 'T00:00:00',
                vkGiltAb: now.format('YYYY-MM-DD') + 'T00:00:00',
            });
        }
    }, [lNr, artikelGruppeNr, artikelNr]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !dbArtikelDetailLieferant.isLoading &&
            !dbArtikelDetailLieferant.isFetching &&
            artikelDetailLieferant !== null &&
            !isEqual(dbArtikelDetailLieferant.data, artikelDetailLieferant)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('artikelDetailLieferant', artikelDetailLieferant, dbArtikelDetailLieferant);
        }
    }, [savingState, dbArtikelDetailLieferant, artikelDetailLieferant]);

    const kunde = useKunde(
        {
            nr: artikelDetailLieferant?.lNr || 0,
        },
        { enabled: !!artikelDetailLieferant?.lNr && artikelDetailLieferant?.lNr !== -1 },
    );

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const save = () => {
        if (artikelDetailLieferant && savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            if (lNr === -1) {
                addArtikelDetailLieferant(artikelDetailLieferant)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            } else {
                updateArtikelDetailLieferant(artikelDetailLieferant)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            }
        }
    };

    const renderLieferantSelect = () => (
        <KundeSelect
            label="Lieferant"
            kNr={artikelDetailLieferant?.lNr}
            name={kunde.data?.name1 || ''}
            disabled={savingState === SavingState.saving || lNr !== -1}
            onChange={(newLNr): void => {
                if (artikelDetailLieferant && artikelDetailLieferant.lNr !== newLNr) {
                    setArtikelDetailLieferant({
                        ...artikelDetailLieferant,
                        lNr: newLNr || 0,
                    });
                }
            }}
        />
    );

    const renderTextField = (
        column: keyof ArtikelDetailLieferant,
        label: string,
        options: FormTextFieldOptions = {},
    ) => (
        <FormTextField
            value={(artikelDetailLieferant?.[column] || '') as string}
            savingState={savingState}
            options={options}
            onChange={(newValue): void => {
                if (artikelDetailLieferant) {
                    setArtikelDetailLieferant({
                        ...artikelDetailLieferant,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderCheckbox = (column: keyof ArtikelDetailLieferant, label: string) => (
        <Checkbox
            className={formStyles.checkbox}
            label={label}
            disabled={savingState === SavingState.saving}
            checked={artikelDetailLieferant?.[column] as boolean}
            onChange={(event, checked) => {
                if (artikelDetailLieferant) {
                    setArtikelDetailLieferant({
                        ...artikelDetailLieferant,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    return (
        <Dialog
            hidden={false}
            title={`Lieferant für Artikel ${artikelGruppeByNr[artikelGruppeNr].text || artikelGruppeNr} | ${artikelNr}`}
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={800}
            onDismiss={() => {
                onClose(false);
            }}
        >
            {routerConfirmMessage.render()}

            {(dbArtikelDetailLieferant.isLoading || dbArtikelDetailLieferant.isFetching) && <LoadingIndicator />}
            {artikelDetailLieferant && (
                <form className={formStyles.form}>
                    <div className={formStyles.line}>
                        <div className={formStyles.gridCell}>
                            {renderLieferantSelect()}
                            {kunde.data ? <OpenLinkIcon link={createKundeUrl(kunde.data?.nr.toString())} /> : null}
                        </div>
                    </div>
                    <div className={formStyles.line}>
                        <div className={formStyles.gridCell}>
                            {renderTextField('lieferantArtikelNr', 'Artikel-Nr. - Lieferant', { maxLength: 25 })}
                        </div>
                    </div>
                    <div className={formStyles.line}>
                        <div className={formStyles.gridCell}>{renderCheckbox('art', 'Hauptlieferant')}</div>
                    </div>
                    <Timestamps
                        modifiedDate={artikelDetailLieferant?.updDat}
                        modifiedDecimalTime={artikelDetailLieferant?.updUhr}
                        modifiedUser={artikelDetailLieferant?.updUser}
                        showCreated={false}
                    />
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                    <DialogFooter>
                        {savingState === SavingState.saving ? (
                            <PrimaryButton onClick={save} text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={save}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || dbArtikelDetailLieferant.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
            )}
        </Dialog>
    );
};
